/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus } from 'lucide-react';
import { productsApi, reviewsApi } from '../lib/api';
import { useCart } from '../context/CartContext';
import ProductGallery from '../components/ProductGallery';
import ProductAccordion from '../components/ProductAccordion';
import ReviewsSection from '../components/Reviews/ReviewsSection';
import StarRating from '../components/Reviews/StarRating';

const FINISH_LABEL = { matte: 'Matte', satin: 'Satin', sheer: 'Sheer', gloss: 'Gloss' };

const DEFAULT_INGREDIENTS = 'Ricinus Communis (Castor) Seed Oil, Hydrogenated Polyisobutene, Caprylic/Capric Triglyceride, Beeswax, Carnauba Wax, Tocopherol (Vitamin E), Rosa Damascena Flower Oil, Iron Oxides (CI 77491, CI 77492, CI 77499), Titanium Dioxide (CI 77891), Mica.';
const DEFAULT_WEIGHT = '3.5 g / 0.12 oz · Net wt.';

const Product = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({ average: 0, total: 0 });

  useEffect(() => {
    setNotFound(false);
    productsApi.get(id).then(async (p) => {
      setProduct(p);
      try {
        const rel = await productsApi.list({ category: p.category });
        setRelated(rel.filter((r) => r.id !== p.id).slice(0, 4));
      } catch (e) { /* ignore related-products failure */ }
      try {
        const s = await reviewsApi.summary(id);
        setRatingSummary({ average: s.average || 0, total: s.total || 0 });
      } catch (e) { /* ignore */ }
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="bg-[#f5f1e6] min-h-screen px-6 lg:px-10 py-24 text-center">
        <p className="font-serif text-3xl">Shade not found.</p>
        <button onClick={() => navigate('/shop')} className="mt-8 text-[11px] tracking-[0.28em] border-b border-[#1a1410] pb-1">
          BACK TO SHOP
        </button>
      </main>
    );
  }
  if (!product) {
    return <main className="bg-[#f5f1e6] min-h-screen px-6 lg:px-10 py-24 text-center text-[#7a6a5a]">Loading…</main>;
  }

  return (
    <main className="bg-[#f5f1e6] text-[#1a1410]">
      <div className="px-6 lg:px-10 pt-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] text-[#3a2e26] border-b border-[#cdc2b1] pb-1 hover:text-[#1a1410]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.4} /> BACK TO SHOP
        </Link>
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 px-6 lg:px-10 py-12">
        <div>
          <ProductGallery
            images={(product.images && product.images.length > 0) ? product.images : (product.image ? [product.image] : [])}
            alt={product.name}
          />
        </div>

        <div className="flex flex-col">
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block self-start uppercase">
            {product.category}
          </div>
          <h1 className="font-serif text-[44px] lg:text-[56px] leading-[1.05] mt-6">{product.name}</h1>
          {ratingSummary.total > 0 && (
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('reviews-section');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="mt-3 inline-flex items-center gap-2 text-sm text-[#3a2e26] hover:text-[#1a1410]"
            >
              <StarRating value={ratingSummary.average} size={14} />
              <span className="font-medium">{ratingSummary.average.toFixed(1)}</span>
              <span className="text-[#7a6a5a]">· {ratingSummary.total} {ratingSummary.total === 1 ? 'review' : 'reviews'}</span>
            </button>
          )}
          <p className="mt-5 text-[15px] text-[#3a2e26]">
            Shade — <span className="text-[#1a1410]">{product.shade}</span>
            <span className="inline-block w-3 h-3 rounded-full ml-3 align-middle" style={{ backgroundColor: product.swatch }} />
          </p>
          {product.sku ? (
            <p className="mt-2 text-xs tracking-[0.18em] uppercase text-[#7a6a5a]" data-testid="product-sku">
              SKU: <span className="font-mono normal-case tracking-normal text-[#3a2e26]">{product.sku}</span>
            </p>
          ) : null}
          <div className="font-medium text-[26px] mt-6 tabular-nums" data-testid="product-price">Rs. {product.price.toFixed(2)}</div>
          {product.short_description?.trim() ? (
            <p
              className="mt-8 max-w-md text-[15px] leading-[1.8] text-[#3a2e26] whitespace-pre-line"
              data-testid="product-short-description"
            >
              {product.short_description.trim()}
            </p>
          ) : null}

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border border-[#cdc2b1]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3"><Minus className="w-3 h-3" strokeWidth={1.4} /></button>
              <span className="px-4 text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3"><Plus className="w-3 h-3" strokeWidth={1.4} /></button>
            </div>
            <button onClick={() => addItem(product, qty)}
              className="flex-1 bg-[#1a1410] text-[#f5f1e6] py-4 text-[11px] tracking-[0.32em] hover:bg-black transition-colors">
              ADD TO BAG
            </button>
          </div>

          <div className="mt-12 pt-2">
            <ProductAccordion
              defaultOpenKey="description"
              sections={[
                {
                  key: 'description',
                  label: 'Description',
                  content: (
                    <div>
                      <p className="whitespace-pre-wrap">{product.description}</p>
                    </div>
                  ),
                },
                {
                  key: 'shade',
                  label: 'Shade',
                  content: (
                    <div>
                      <div className="flex items-center gap-4">
                        <span
                          className="inline-block w-12 h-12 rounded-full border border-[#cdc2b1]"
                          style={{ backgroundColor: product.swatch }}
                          aria-hidden="true"
                        />
                        <div>
                          <div className="font-serif text-2xl text-[#1a1410] leading-tight">{product.shade}</div>
                          <div className="text-xs tracking-[0.18em] uppercase text-[#7a6a5a] mt-1">
                            {FINISH_LABEL[product.category] || product.category} finish · Hex {product.swatch}
                          </div>
                        </div>
                      </div>
                      <p className="mt-5 text-[14px]">
                        {product.shade} is composed in our {FINISH_LABEL[product.category]?.toLowerCase() || product.category} finish — pigment-rich, cushioned, and wear-tested across diverse undertones for true-to-life colour on every complexion.
                      </p>
                    </div>
                  ),
                },
                {
                  key: 'ingredients',
                  label: 'Ingredients',
                  content: (
                    <div>
                      <p>{product.ingredients?.trim() || DEFAULT_INGREDIENTS}</p>
                      <p className="mt-4 text-xs text-[#7a6a5a] leading-relaxed">
                        Clean composition · Paraben-free · Cruelty-free · Vegan formula. Full ingredient list (INCI) printed on every carton.
                      </p>
                    </div>
                  ),
                },
                {
                  key: 'weight',
                  label: 'Weight',
                  content: (
                    <div>
                      <p>{product.weight?.trim() || DEFAULT_WEIGHT}</p>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Reviews */}
      <div id="reviews-section">
        <ReviewsSection product={product} />
      </div>

      {related.length > 0 && (
        <section className="px-6 lg:px-10 py-20">
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block mb-10">YOU MAY ALSO LIKE</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
            {related.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group block">
                <div className="overflow-hidden bg-[#e9e2d3] aspect-[3/4]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                </div>
                <div className="mt-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#7a6a5a]">{p.category}</div>
                  <div className="font-serif text-[22px] mt-1">{p.name}</div>
                  <div className="font-medium text-[15px] text-[#3a2e26] mt-1 tabular-nums">Rs. {p.price.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default Product;
